import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import ProductWrapper from '../../components/Product/ProductWrapper';
import Masonry from 'react-masonry-css';
import { ClipLoader } from 'react-spinners';
import throttle from 'lodash.throttle';
import './SearchPage.css';

const ITEMS_PER_LOAD = 10;

function SearchPage() {
  const { text } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadIndexRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);

  // Configuración de breakpoints para Masonry
  const breakpointColumnsObj = {
    default: 6,
    1600: 6,
    1200: 5,
    768: 3,
    480: 2
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    setTimeout(() => {
      const next = products.slice(
        loadIndexRef.current,
        loadIndexRef.current + ITEMS_PER_LOAD
      );
      setVisibleProducts(prev => [...prev, ...next]);
      loadIndexRef.current += ITEMS_PER_LOAD;
      if (loadIndexRef.current >= products.length) {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 500);
  }, [products, loadingMore, hasMore]);

  const handleScroll = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;

    if (
      window.innerHeight +
      document.documentElement.scrollTop +
      200 >=
      document.documentElement.offsetHeight
    ) {
      loadMore();
    }
  }, [loading, loadingMore, hasMore, loadMore]);

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        setLoading(true);
        setVisibleProducts([]);
        loadIndexRef.current = 0;
        setHasMore(true);
        
        if (!text || text.trim() === '') {
          navigate('/');
          return;
        }

        // Throttling: solo buscar cada 2 segundos para el mismo término
        const searchKey = text.trim().toLowerCase();
        const lastSearch = window._searchCache?.[searchKey] || 0;
        const now = Date.now();
        if (now - lastSearch < 2000) {
          setLoading(false);
          return;
        }
        
        if (!window._searchCache) window._searchCache = {};
        window._searchCache[searchKey] = now;

        const searchRes = await fetch(`https://api.minymol.com/products/search/${encodeURIComponent(text.trim())}`);
        
        if (!searchRes.ok) {
          if (searchRes.status === 429) {
            setLoading(false);
            return; // Silenciar rate limiting
          }
          throw new Error(`HTTP ${searchRes.status}`);
        }
        
        const data = await searchRes.json();

        if (!Array.isArray(data)) throw new Error('Respuesta inválida del backend');

        const ids = data.map(p => p.product_id);

        const previewsRes = await fetch('https://api.minymol.com/products/previews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids })
        });
        
        if (!previewsRes.ok) {
          if (previewsRes.status === 429) {
            setLoading(false);
            return; // Silenciar rate limiting
          }
          throw new Error(`HTTP ${previewsRes.status}`);
        }

        const previews = await previewsRes.json();
        setProducts(previews);
      } catch (error) {
        // Silenciar errores de rate limiting
        if (!error.message?.includes('429')) {
          console.error('Error al cargar productos:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProductos();
  }, [text, navigate]);

  useEffect(() => {
    if (!loading && products.length > 0) {
      const inicial = products.slice(0, ITEMS_PER_LOAD);
      setVisibleProducts(inicial);
      loadIndexRef.current = ITEMS_PER_LOAD;
      setHasMore(products.length > ITEMS_PER_LOAD);
    }
  }, [loading, products]);

  useEffect(() => {
    const throttledScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      throttledScroll.cancel();
    };
  }, [handleScroll]);

  return (
    <div className="search-page-container">
      <Header />
      <p className='search-title'>
        Resultados de la búsqueda para <strong>"{text}"</strong>
      </p>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
          <ClipLoader color="#fa7e17" size={30} />
        </div>
      ) : (
        <>
          {visibleProducts.length > 0 ? (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="products-cont masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {visibleProducts.map((prod, index) => (
                <ProductWrapper key={`wrapper-${index}`} product={prod} index={index} />
              ))}
            </Masonry>
          ) : (
            <p style={{ textAlign: 'center', width: '100%' }}>No se encontraron productos.</p>
          )}
          {loadingMore && hasMore && (
            <div className="loader-bottom" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
              <ClipLoader color="#fa7e17" size={25} />
            </div>
          )}
          {!hasMore && visibleProducts.length > 0 && (
            <div style={{ textAlign: 'center', color: '#888', padding: '1rem' }}>
              No hay más productos para mostrar
            </div>
          )}
        </>
      )}
      <NavInf selected={"home"} />
      <Footer />
    </div>
  );
}

export default SearchPage;
